import { Link } from '@remix-run/react';

const HowItWorks = () => {
	return (
		<section id="how-it-works" className="mt-6 px-6 pb-12 lg:mt-12 lg:px-36">
			<div className="flex flex-col items-center justify-center rounded-2xl">
				<div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 pt-10 lg:pb-12">
					<h2 className="text-transparent mx-auto bg-clip-text text-center text-3xl font-extrabold sm:text-4xl lg:text-5xl lg:leading-[60px]">
						<span className="text-transparent bg-gradient-to-b from-white from-70% to-[#b2b2b2] bg-clip-text">
							Get your product{' '}
						</span>
						<span className="text-transparent bg-gradient-to-b from-white from-30% to-[#7496F8] bg-clip-text">
							live on your website{' '}
						</span>
						<span className="text-transparent bg-gradient-to-b from-white from-70% to-[#b2b2b2] bg-clip-text">
							in just 60 seconds
						</span>
					</h2>

					<div className="mx-auto max-w-[325px] text-center text-lg text-white md:max-w-full lg:text-xl">
						Let PagePilot.ai do the hard work for you. It's this easy:
					</div>
				</div>

				<div className="grid max-w-7xl grid-cols-1 items-start justify-center justify-items-center gap-6 md:grid-cols-2 md:gap-20 lg:grid-cols-3">
					<div className="relative flex flex-col items-center justify-center px-2">
						<div className="card-bg mt-6 w-full max-w-[350px]">
							<div className="inner px-6 py-8">
								<div className="mb-2 text-sm font-medium text-white">Product source</div>
								<div className="mb-4 flex gap-6">
									<div className="card-bg items-center rounded-lg px-4 py-4">
										<img
											src="/images/aliexpress.svg"
											className="h-full w-full"
											alt="Aliexpress Logo"
											width="100"
											height="30"
										/>
									</div>

									<div className="card-bg items-center rounded-lg px-4 py-4">
										<img
											src="/images/landingpage/shopifyLogoWhite.svg"
											className="h-full w-full"
											alt="Shopify Logo"
											width="100"
											height="30"
										/>
									</div>
								</div>
								<div className="flex flex-col">
									<div className="mb-2 text-sm font-medium text-white">Product URL</div>
									<input
										value="https://competitorwebsite.com/product/......"
										className="card-bg rounded-lg px-2 py-3 text-sm text-slate-400"
										disabled
									/>
								</div>

								<button className="btn-primary mt-6 w-full opacity-50">Generate Product Page</button>
							</div>
						</div>

						<img
							src="/images/landingpage/step1.svg"
							className="mx-auto -mb-3 mt-9 w-full max-w-[30px]"
							alt="Step 1"
							width="30"
							height="30"
						/>
						<div className="max-w-[350px] text-center text-2xl font-bold tracking-tight text-white">
							Copy the product link from AliExpress or Shopify
						</div>
						<p className="mt-6 text-center text-gray-DEFAULT-400">
							PagePilot.ai automatically extracts all the relevant product information, saving you the effort.
						</p>
					</div>

					<div className="relative flex flex-col items-center justify-center px-2">
						<div className="card-bg mt-6 w-full max-w-[350px]">
							<div className="inner px-6 py-8">
								<div className="grid grid-cols-2 gap-4">
									<div className="w-full">
										<div className="card-bg h-28 animate-pulse items-center justify-center">
											<svg
												className="h-8 w-8 fill-white/30"
												aria-hidden="true"
												xmlns="http://www.w3.org/2000/svg"
												fill="currentColor"
												viewBox="0 0 20 18">
												<path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
											</svg>
										</div>
										<div className="mt-2 flex justify-between">
											<div className="card-bg h-7 w-7 animate-pulse items-center justify-center rounded-lg">
												<svg
													className="h-3 w-3 fill-white/30"
													aria-hidden="true"
													xmlns="http://www.w3.org/2000/svg"
													fill="currentColor"
													viewBox="0 0 20 18">
													<path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
												</svg>
											</div>

											<div className="card-bg h-7 w-7 animate-pulse items-center justify-center rounded-lg">
												<svg
													className="h-3 w-3 fill-white/30"
													aria-hidden="true"
													xmlns="http://www.w3.org/2000/svg"
													fill="currentColor"
													viewBox="0 0 20 18">
													<path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
												</svg>
											</div>

											<div className="card-bg h-7 w-7 animate-pulse items-center justify-center rounded-lg">
												<svg
													className="h-3 w-3 fill-white/30"
													aria-hidden="true"
													xmlns="http://www.w3.org/2000/svg"
													fill="currentColor"
													viewBox="0 0 20 18">
													<path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
												</svg>
											</div>

											<div className="card-bg h-7 w-7 animate-pulse items-center justify-center rounded-lg">
												<svg
													className="h-3 w-3 fill-white/30"
													aria-hidden="true"
													xmlns="http://www.w3.org/2000/svg"
													fill="currentColor"
													viewBox="0 0 20 18">
													<path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
												</svg>
											</div>
										</div>
									</div>
									<div className="w-full">
										<div className="flex items-center gap-2">
											<div className="flex animate-pulse gap-[2px]">
												<svg
													className="h-[10px] w-[10px] fill-white/30"
													viewBox="0 0 17 16"
													fill="none"
													xmlns="http://www.w3.org/2000/svg">
													<path
														fillRule="evenodd"
														clipRule="evenodd"
														d="M13.7762 9.68076C13.4346 10.0107 13.2794 10.4861 13.3594 10.9524L13.8754 13.936C14.0362 14.8648 13.2922 15.619 12.4482 15.619C12.2258 15.619 11.9962 15.5665 11.7738 15.4507L9.07379 14.0419C8.86259 13.9322 8.63139 13.8773 8.39939 13.8773C8.16819 13.8773 7.93699 13.9322 7.72579 14.0419L5.02579 15.4507C4.80339 15.5665 4.57379 15.619 4.35139 15.619C3.50738 15.619 2.76339 14.8648 2.92419 13.936L3.44019 10.9524C3.52019 10.4861 3.36499 10.0107 3.02339 9.68076L0.838583 7.568C-0.0206178 6.73676 0.453785 5.28838 1.64099 5.11696L4.66019 4.68191C5.13219 4.6141 5.54019 4.32 5.75059 3.89562L7.10099 1.18171C7.36659 0.647615 7.88339 0.380952 8.39939 0.380952C8.91619 0.380952 9.43299 0.647615 9.69859 1.18171L11.049 3.89562C11.2594 4.32 11.6674 4.6141 12.1394 4.68191L15.1586 5.11696C16.3458 5.28838 16.8202 6.73676 15.961 7.568L13.7762 9.68076Z"
													/>
												</svg>
												<svg
													className="h-[10px] w-[10px] fill-white/30"
													viewBox="0 0 17 16"
													fill="none"
													xmlns="http://www.w3.org/2000/svg">
													<path
														fillRule="evenodd"
														clipRule="evenodd"
														d="M13.7762 9.68076C13.4346 10.0107 13.2794 10.4861 13.3594 10.9524L13.8754 13.936C14.0362 14.8648 13.2922 15.619 12.4482 15.619C12.2258 15.619 11.9962 15.5665 11.7738 15.4507L9.07379 14.0419C8.86259 13.9322 8.63139 13.8773 8.39939 13.8773C8.16819 13.8773 7.93699 13.9322 7.72579 14.0419L5.02579 15.4507C4.80339 15.5665 4.57379 15.619 4.35139 15.619C3.50738 15.619 2.76339 14.8648 2.92419 13.936L3.44019 10.9524C3.52019 10.4861 3.36499 10.0107 3.02339 9.68076L0.838583 7.568C-0.0206178 6.73676 0.453785 5.28838 1.64099 5.11696L4.66019 4.68191C5.13219 4.6141 5.54019 4.32 5.75059 3.89562L7.10099 1.18171C7.36659 0.647615 7.88339 0.380952 8.39939 0.380952C8.91619 0.380952 9.43299 0.647615 9.69859 1.18171L11.049 3.89562C11.2594 4.32 11.6674 4.6141 12.1394 4.68191L15.1586 5.11696C16.3458 5.28838 16.8202 6.73676 15.961 7.568L13.7762 9.68076Z"
													/>
												</svg>
												<svg
													className="h-[10px] w-[10px] fill-white/30"
													viewBox="0 0 17 16"
													fill="none"
													xmlns="http://www.w3.org/2000/svg">
													<path
														fillRule="evenodd"
														clipRule="evenodd"
														d="M13.7762 9.68076C13.4346 10.0107 13.2794 10.4861 13.3594 10.9524L13.8754 13.936C14.0362 14.8648 13.2922 15.619 12.4482 15.619C12.2258 15.619 11.9962 15.5665 11.7738 15.4507L9.07379 14.0419C8.86259 13.9322 8.63139 13.8773 8.39939 13.8773C8.16819 13.8773 7.93699 13.9322 7.72579 14.0419L5.02579 15.4507C4.80339 15.5665 4.57379 15.619 4.35139 15.619C3.50738 15.619 2.76339 14.8648 2.92419 13.936L3.44019 10.9524C3.52019 10.4861 3.36499 10.0107 3.02339 9.68076L0.838583 7.568C-0.0206178 6.73676 0.453785 5.28838 1.64099 5.11696L4.66019 4.68191C5.13219 4.6141 5.54019 4.32 5.75059 3.89562L7.10099 1.18171C7.36659 0.647615 7.88339 0.380952 8.39939 0.380952C8.91619 0.380952 9.43299 0.647615 9.69859 1.18171L11.049 3.89562C11.2594 4.32 11.6674 4.6141 12.1394 4.68191L15.1586 5.11696C16.3458 5.28838 16.8202 6.73676 15.961 7.568L13.7762 9.68076Z"
													/>
												</svg>
												<svg
													className="h-[10px] w-[10px] fill-white/30"
													viewBox="0 0 17 16"
													fill="none"
													xmlns="http://www.w3.org/2000/svg">
													<path
														fillRule="evenodd"
														clipRule="evenodd"
														d="M13.7762 9.68076C13.4346 10.0107 13.2794 10.4861 13.3594 10.9524L13.8754 13.936C14.0362 14.8648 13.2922 15.619 12.4482 15.619C12.2258 15.619 11.9962 15.5665 11.7738 15.4507L9.07379 14.0419C8.86259 13.9322 8.63139 13.8773 8.39939 13.8773C8.16819 13.8773 7.93699 13.9322 7.72579 14.0419L5.02579 15.4507C4.80339 15.5665 4.57379 15.619 4.35139 15.619C3.50738 15.619 2.76339 14.8648 2.92419 13.936L3.44019 10.9524C3.52019 10.4861 3.36499 10.0107 3.02339 9.68076L0.838583 7.568C-0.0206178 6.73676 0.453785 5.28838 1.64099 5.11696L4.66019 4.68191C5.13219 4.6141 5.54019 4.32 5.75059 3.89562L7.10099 1.18171C7.36659 0.647615 7.88339 0.380952 8.39939 0.380952C8.91619 0.380952 9.43299 0.647615 9.69859 1.18171L11.049 3.89562C11.2594 4.32 11.6674 4.6141 12.1394 4.68191L15.1586 5.11696C16.3458 5.28838 16.8202 6.73676 15.961 7.568L13.7762 9.68076Z"
													/>
												</svg>
												<svg
													className="h-[10px] w-[10px] fill-white/30"
													viewBox="0 0 17 16"
													fill="none"
													xmlns="http://www.w3.org/2000/svg">
													<path
														fillRule="evenodd"
														clipRule="evenodd"
														d="M13.7762 9.68076C13.4346 10.0107 13.2794 10.4861 13.3594 10.9524L13.8754 13.936C14.0362 14.8648 13.2922 15.619 12.4482 15.619C12.2258 15.619 11.9962 15.5665 11.7738 15.4507L9.07379 14.0419C8.86259 13.9322 8.63139 13.8773 8.39939 13.8773C8.16819 13.8773 7.93699 13.9322 7.72579 14.0419L5.02579 15.4507C4.80339 15.5665 4.57379 15.619 4.35139 15.619C3.50738 15.619 2.76339 14.8648 2.92419 13.936L3.44019 10.9524C3.52019 10.4861 3.36499 10.0107 3.02339 9.68076L0.838583 7.568C-0.0206178 6.73676 0.453785 5.28838 1.64099 5.11696L4.66019 4.68191C5.13219 4.6141 5.54019 4.32 5.75059 3.89562L7.10099 1.18171C7.36659 0.647615 7.88339 0.380952 8.39939 0.380952C8.91619 0.380952 9.43299 0.647615 9.69859 1.18171L11.049 3.89562C11.2594 4.32 11.6674 4.6141 12.1394 4.68191L15.1586 5.11696C16.3458 5.28838 16.8202 6.73676 15.961 7.568L13.7762 9.68076Z"
													/>
												</svg>
											</div>
											<div className="h-2 w-full animate-pulse rounded-full bg-white/10"></div>
										</div>

										<div className="mt-4 flex flex-col">
											<div className="h-4 w-full animate-pulse rounded-full bg-white/10 "></div>
											<div className="mt-2 h-2 w-full animate-pulse  rounded-full bg-white/10"></div>

											<div className="mt-4 h-2 w-full max-w-[70px] animate-pulse rounded-full bg-white/10"></div>
											<div className="mt-2 h-2 w-full max-w-[70px] animate-pulse rounded-full bg-white/10"></div>

											<div className="mt-4 h-4 w-full animate-pulse rounded-full bg-white/10"></div>

											<div className="mt-2 flex gap-2">
												<div className="h-2 w-full animate-pulse rounded-full bg-white/10"></div>
												<div className="h-2 w-full animate-pulse rounded-full bg-white/10"></div>
												<div className="h-2 w-full animate-pulse rounded-full bg-white/10"></div>
											</div>
										</div>
									</div>
								</div>
								<div className="mt-8 flex items-center gap-4">
									<div className="mt-2 flex w-full flex-col gap-2">
										<div className="h-2 w-full max-w-[50px] animate-pulse rounded-full bg-white/10"></div>
										<div className="h-4 w-full max-w-[100px] animate-pulse rounded-full bg-white/10"></div>
										<div className="h-2 w-full animate-pulse rounded-full bg-white/10"></div>
										<div className="h-2 w-full animate-pulse rounded-full bg-white/10"></div>
									</div>

									<div className="card-bg h-28 w-full animate-pulse items-center justify-center">
										<svg
											className="h-8 w-8 fill-white/30"
											aria-hidden="true"
											xmlns="http://www.w3.org/2000/svg"
											fill="currentColor"
											viewBox="0 0 20 18">
											<path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
										</svg>
									</div>
								</div>
							</div>
						</div>

						<img
							src="/images/landingpage/step2.svg"
							className="mx-auto -mb-3 mt-9 w-full max-w-[50px]"
							alt="Step 2"
							width="50"
							height="50"
						/>
						<div className="max-w-[350px] text-center text-2xl font-bold tracking-tight text-white">
							Let PagePilot.ai automatically extract and optimize product information
						</div>
						<p className="mt-6 text-center text-gray-DEFAULT-400">
							Our AI creates a sales-oriented page with optimal sections, reviews, variants, guarantees, and images.
						</p>
					</div>

					<div className="relative flex flex-col items-center justify-center px-2 md:col-span-2 lg:col-auto">
						<div className="card-bg mt-6 w-full max-w-[350px]">
							<div className="inner px-6 py-8">
								<div className="flex flex-col">
									<div className="mb-2 text-sm font-medium text-white">Select Shopify Store</div>
									<input
										value="My Awesome Store"
										className="card-bg rounded-lg px-2 py-3 text-sm text-slate-400"
										disabled
									/>
								</div>

								<div className="mt-4">
									<div className="mb-2 text-sm font-medium text-white">Select Shopify Store</div>

									<div className="card-bg mt-4 flex gap-4 rounded-lg px-2 py-3">
										<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 px-2 py-3">
											<svg className="h-10 w-10 fill-white/80" viewBox="0 0 256 256">
												<path d="M200.77,53.89A103.27,103.27,0,0,0,128,24h-1.07A104,104,0,0,0,24,128c0,43,26.58,79.06,69.36,94.17A32,32,0,0,0,136,192a16,16,0,0,1,16-16h46.21a31.81,31.81,0,0,0,31.2-24.88,104.43,104.43,0,0,0,2.59-24A103.28,103.28,0,0,0,200.77,53.89ZM84,168a12,12,0,1,1,12-12A12,12,0,0,1,84,168Zm0-56a12,12,0,1,1,12-12A12,12,0,0,1,84,112Zm44-24a12,12,0,1,1,12-12A12,12,0,0,1,128,88Zm44,24a12,12,0,1,1,12-12A12,12,0,0,1,172,112Z"></path>
											</svg>
										</div>
										<div className="flex flex-col">
											<div className="text-sm text-white">Import into Theme Templates</div>
											<div className="mt-1 text-xs text-slate-400">Edit it using Customizer</div>
										</div>
									</div>

									<div className="card-bg mt-4 flex gap-4 rounded-lg px-2 py-3">
										<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 px-2 py-3">
											<svg className="h-10 w-10 fill-white/80" viewBox="0 0 256 256">
												<path d="M227.32,73.37,182.63,28.69a16,16,0,0,0-22.63,0L36.69,152A15.86,15.86,0,0,0,32,163.31V208a16,16,0,0,0,16,16H216a8,8,0,0,0,0-16H115.32l112-112A16,16,0,0,0,227.32,73.37ZM79.32,188l90.34-90.34,16.69,16.68L96,204.69Zm79-101.66h0L68,176.69,51.31,160l90.35-90.34ZM48,179.31,76.69,208H48Z"></path>
											</svg>
										</div>
										<div className="flex flex-col">
											<div className="text-sm text-white">Import into Product Description</div>
											<div className="mt-1 text-xs text-slate-400">Edit from product description</div>
										</div>
									</div>
								</div>

								<button className="btn-primary mt-6 w-full opacity-50">Import in Shopify</button>
							</div>
						</div>

						<img
							src="/images/landingpage/step3.svg"
							className="mx-auto -mb-3 mt-9 w-full max-w-[50px]"
							alt="Step 3"
							width="50"
							height="50"
						/>
						<div className="max-w-[350px] text-center text-2xl font-bold tracking-tight text-white">
							Get a high-converting product page in seconds and import it to your Shopify store with one click.
						</div>
						<p className="mt-6 text-center text-gray-DEFAULT-400">
							It's as simple as it sounds. The generated product page will be live on your store within seconds.
						</p>
					</div>
				</div>

				<div className="mx-auto mt-6 flex flex-col justify-center gap-2 lg:mt-12">
					<div className="mt-6 flex flex-row flex-wrap items-center justify-center md:mt-0">
						<div>
							<div className="flex flex-wrap items-center justify-center gap-1 text-sm font-light text-white lg:gap-3 lg:text-lg">
								<div className="flex lg:hidden">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 20"
										fill="currentColor"
										className="h-4 w-4 text-[#FFA132] lg:h-5 lg:w-5">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
									</svg>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 20"
										fill="currentColor"
										className="h-4 w-4 text-[#FFA132] lg:h-5 lg:w-5">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
									</svg>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 20"
										fill="currentColor"
										className="h-4 w-4 text-[#FFA132] lg:h-5 lg:w-5">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
									</svg>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 20"
										fill="currentColor"
										className="h-4 w-4 text-[#FFA132] lg:h-5 lg:w-5">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
									</svg>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 20"
										fill="currentColor"
										className="h-4 w-4 text-[#FFA132] lg:h-5 lg:w-5">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
									</svg>
								</div>
								<div>
									<span className="hidden lg:inline-flex">Rated</span> "Excellent"{' '}
									<span className="font-bold">4.8/5</span>
								</div>
								<div className="hidden flex-row lg:flex">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 20"
										fill="currentColor"
										className="h-4 w-4 text-[#FFA132] lg:h-5 lg:w-5">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
									</svg>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 20"
										fill="currentColor"
										className="h-4 w-4 text-[#FFA132] lg:h-5 lg:w-5">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
									</svg>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 20"
										fill="currentColor"
										className="h-4 w-4 text-[#FFA132] lg:h-5 lg:w-5">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
									</svg>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 20"
										fill="currentColor"
										className="h-4 w-4 text-[#FFA132] lg:h-5 lg:w-5">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
									</svg>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 20"
										fill="currentColor"
										className="h-4 w-4 text-[#FFA132] lg:h-5 lg:w-5">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
									</svg>
								</div>
								by our <span className="font-bold">1000+</span> successful dropshippers
							</div>
							<div className="mb-4 mt-2 text-center text-white">
								Discover Why PagePilot is the Preferred Choice for Dropshippers.
							</div>
						</div>
					</div>
					<Link
						prefetch="intent"
						className="btn-landing mx-auto w-full max-w-full text-center font-semibold sm:max-w-[400px]"
						to="/signup">
						Start Listing Products Today
					</Link>
					<div className="text-center text-gray-DEFAULT-400">
						Risk-Free Trial - Start Creating Winning Product Pages Today with No Credit Card Required!
					</div>
				</div>
			</div>
		</section>
	);
};

export default HowItWorks;
